import React, { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  FieldsSection,
  FormNumericField,
  FormSelect,
} from '../../../../../../shared/components/form';
import { useTranslation } from 'react-i18next';
import { Button, Stack } from '@mui/material';
import { C_Rounding_Method } from '../../../../../../graphql/generatedModel.ts';
import { configurationTabState } from '../../../states/configuration.state.ts';
import { TCompanyConfigs } from '../../../../../../shared/services/companyConfig/companyConfig.service.ts';
import { ISelectProps } from '../../../../../../shared/components/form/fields/select.component.tsx';
import { QuantityDiscountPopup } from './dictionaries/quantityDiscountsDict/popup/quantityDiscount.popup.tsx';
import { TextEditCellDict } from '../../../../../../shared/components/dictionary/cells/textEditDict.cell.tsx';
import { DescriptionDictCell } from './dictionaries/quantityDiscountsDict/cells/descriptionDict.cell.tsx';
import { DictionaryPopup } from '../../../../../../shared/components/dictionary/dictionary.popup.tsx';
import { DebtReminderSettingPopup } from './dictionaries/debtRemindersDict/popup/debtReminderSetting.popup.tsx';
import { DescriptionDebtReminder } from './dictionaries/debtRemindersDict/cells/descriptionDebtReminder.cell.tsx';

export const InvoiceTab: FC<IInvoiceTab> = ({ params }) => {
  const { t } = useTranslation();
  const dictionaryPopup = useMemo(() => new DictionaryPopup(), []);
  const debtReminderSettingPopup = useMemo(() => new DebtReminderSettingPopup(), []);
  const quantityDiscountPopup = useMemo(() => new QuantityDiscountPopup(), []);
  const formMethods = useForm({
    defaultValues: params,
    mode: 'onChange',
  });
  const {
    resetField,
    formState: { dirtyFields },
  } = formMethods;
  const priceRoundingOptions: ISelectProps['options'] = useMemo(
    () => [
      {
        id: C_Rounding_Method.RM1_NOT_USED,
        label: t(`enums.${C_Rounding_Method.RM1_NOT_USED}`),
      },
      {
        id: C_Rounding_Method.RM2_TO_0_05,
        label: t(`enums.${C_Rounding_Method.RM2_TO_0_05}`),
      },
      {
        id: C_Rounding_Method.RM3_TO_0_01,
        label: t(`enums.${C_Rounding_Method.RM3_TO_0_01}`),
      },
      {
        id: C_Rounding_Method.RM4_TO_0_001,
        label: t(`enums.${C_Rounding_Method.RM4_TO_0_001}`),
      },
      {
        id: C_Rounding_Method.RM5_TO_0_1,
        label: t(`enums.${C_Rounding_Method.RM5_TO_0_1}`),
      },
    ],
    [t, C_Rounding_Method],
  );

  useEffect(() => {
    const formStateSub = formMethods.watch((data: Record<string, any>, reason) => {
      if (reason.type === 'change' && reason.name) {
        const fieldName = reason.name;
        let dataToSave = { [fieldName]: data[fieldName] };
        configurationTabState.pub.recordData(dataToSave);
      }
    });
    const configurationTabStateSub = configurationTabState.sub
      .state()
      .subscribe(({ dataToSave }) => {
        for (const valuesKey in dataToSave) {
          if (valuesKey && dataToSave) {
            resetField(valuesKey as keyof TCompanyConfigs, {
              defaultValue: dataToSave[valuesKey as keyof typeof dataToSave],
              keepDirty: false,
              keepError: true,
            });
          }
        }
      });

    return () => {
      formStateSub.unsubscribe();
      configurationTabStateSub.unsubscribe();
      dictionaryPopup.stream.unsubscribe();
      quantityDiscountPopup.stream.unsubscribe();
      debtReminderSettingPopup.stream.unsubscribe();
    };
  }, []);

  const onSubmit = (e: React.FocusEvent<HTMLFormElement>) => {
    if (!!dirtyFields[e.target.name as keyof typeof dirtyFields]) {
      configurationTabState.pub.saveData();
    }
  };

  const quantityDiscountsListsHandler = () => {
    dictionaryPopup.stream.emit('open', {
      params: {
        popupTitle: 'settings.quantity_discounts',
        dictionaryType: 'dictQuantityDiscounts',
        validateFields: ['name'],
        columns: [
          {
            field: 'name',
            headerName: t('common.description'),
            renderEditCell: (params) => (
              <TextEditCellDict
                rightButton={{
                  title: t('settings.quantity_discount'),
                  handler: () =>
                    quantityDiscountPopup.stream.emit('open', {
                      id: String(params.id),
                      title: params.row?.name,
                    }),
                }}
                {...params}
              />
            ),
            renderCell: (params) => (
              <DescriptionDictCell popupStream={quantityDiscountPopup.stream} {...params} />
            ),
            editable: true,
          },
        ],
      },
    });
  };

  const debtRemindersHandler = () => {
    dictionaryPopup.stream.emit('open', {
      params: {
        popupTitle: 'settings.reminder_text',
        dictionaryType: 'dictDebtReminders',
        validateFields: ['name'],
        columns: [
          {
            field: 'name',
            headerName: t('common.description'),
            renderEditCell: (params) => (
              <TextEditCellDict
                rightButton={{
                  title: t('settings.reminder_text'),
                  handler: () =>
                    debtReminderSettingPopup.stream.emit('open', {
                      id: String(params.id),
                      title: params.row?.name,
                    }),
                }}
                {...params}
              />
            ),
            renderCell: (params) => (
              <DescriptionDebtReminder popupStream={debtReminderSettingPopup.stream} {...params} />
            ),
            editable: true,
          },
        ],
      },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form noValidate onBlur={onSubmit}>
        <Stack
          direction='column'
          spacing={3}
          sx={{
            minWidth: '300px',
            pt: 1,
          }}
        >
          <FormNumericField
            name='actualInvoiceNo'
            precision={0}
            label={t('settings.actual_invoice_no')}
          />
          <FormSelect
            name='roundingTotalPricesMethodId'
            options={priceRoundingOptions}
            label={t('settings.rounding_total_prices')}
            disableClearable
          />
        </Stack>
      </form>
      <FieldsSection title={t('settings.related_directories')} nomt>
        <Stack
          spacing={1}
          sx={{
            alignItems: 'start',
            width: 'fit-content',
          }}
        >
          <Button
            onClick={quantityDiscountsListsHandler}
            size='medium'
            variant='outlined'
            color='secondary'
            fullWidth
          >
            {t('settings.quantity_discounts_settings')}
          </Button>
          <Button
            onClick={debtRemindersHandler}
            size='medium'
            variant='outlined'
            color='secondary'
            fullWidth
          >
            {t('settings.reminder_texts_settings')}
          </Button>
        </Stack>
      </FieldsSection>
      <dictionaryPopup.Component />
      <quantityDiscountPopup.Component />
      <debtReminderSettingPopup.Component />
    </FormProvider>
  );
};

interface IInvoiceTab {
  params: TCompanyConfigs;
}
